import {
  getOnboardingUrl,
  ONBOARDING_RN_FLAG,
  ONBOARDING_RN_VARIANT,
} from '../config/runtimeConfig';

import { analytics } from './analytics';
import {
  APP_DESTINATIONS,
  getDefaultMemberAppUrl,
  GCS_APP_DESTINATIONS,
  DESTINATION_FLAGS,
  ONBOARDING_RN_PATH,
} from './constants';

/**
 * Resolves the post-login redirect URL for a given next_app key.
 *
 * If the PostHog feature flag for the app is enabled, returns the GCS path
 * (relative URL — same domain as login). Otherwise returns the legacy
 * webserver URL. Gracefully falls back to member_app on unknown keys.
 *
 * Flag names: login-destination-sparta | login-destination-onboarding |
 *             login-destination-delphi | login-destination-hermes
 *
 * Onboarding 2.0 (React Native) override: when nextApp === 'member_onboarding'
 * and `login-destination-onboarding-react-native` resolves to the 'test'
 * variant, route to /onboarding/ regardless of the legacy
 * `login-destination-onboarding` flag. Brian's RN app is GCS-only, so there is
 * no webserver fallback. `onboardingUrl` config short-circuits the flag lookup.
 */
export async function resolveDestination(nextApp: string): Promise<string> {
  if (nextApp === 'member_onboarding') {
    const override = getOnboardingUrl();
    if (override) return override;
    const rnVariant = await analytics.getFeatureFlag(ONBOARDING_RN_FLAG);
    if (rnVariant === ONBOARDING_RN_VARIANT) return ONBOARDING_RN_PATH;
  }

  const webserverUrl = APP_DESTINATIONS[nextApp] ?? getDefaultMemberAppUrl();
  if (!isDestinationApp(nextApp)) return webserverUrl;
  const flagKey = DESTINATION_FLAGS[nextApp];

  const flagEnabled = await analytics.isFeatureFlagEnabled(flagKey);
  return flagEnabled ? (GCS_APP_DESTINATIONS[nextApp] ?? webserverUrl) : webserverUrl;
}

const isDestinationApp = (
  key: string
): key is keyof typeof DESTINATION_FLAGS => key in DESTINATION_FLAGS;

/**
 * Resolves a ?next= URL with feature flag awareness.
 *
 * Relative paths that match a known GCS destination are re-routed through
 * resolveDestination() so the flag controls which domain the user lands on.
 * Absolute URLs and unrecognised relative paths pass through unchanged.
 * Sub-paths are preserved (e.g. /clients/dashboard → /app/dashboard when
 * the API says next_app=member_app).
 *
 * @param url      The ?next= query parameter value
 * @param nextApp  The next_app key from the login API response — when provided,
 *                 overrides the URL-matched app key so the API controls which
 *                 base path the user lands on (e.g. /app/ vs /clients/).
 */
export async function resolveNextUrl(url: string | undefined, nextApp?: string): Promise<string | undefined> {
  if (!url) return undefined;

  // Absolute or protocol-relative — leave for sanitizeRedirectUrl to handle
  if (!url.startsWith('/') || url.startsWith('//')) return url;

  // Split path from query so the prefix match compares paths only. Without
  // this, a deeplinked next like `/app?path=tool/view&…` normalizes to
  // `/app?path=…/` which does not start with `/app/`, so the match falls
  // through and the URL passes back as a relative path — landing the user
  // on the SPA host (app.vida.com) instead of routing through resolveDestination.
  const qIdx = url.indexOf('?');
  const pathOnly = qIdx === -1 ? url : url.slice(0, qIdx);
  const querySuffix = qIdx === -1 ? '' : url.slice(qIdx);

  // Normalize trailing slash so /app matches /app/
  const normalized = pathOnly.endsWith('/') ? pathOnly : pathOnly + '/';

  // Match against known GCS paths — longest prefix first so /clients/onboarding/
  // wins over /clients/ when both could match
  const match = Object.entries(GCS_APP_DESTINATIONS)
    .sort(([, a], [, b]) => b.length - a.length)
    .find(([, gcsPath]) => normalized === gcsPath || normalized.startsWith(gcsPath));

  if (!match) return url; // Unknown relative path — pass through unchanged

  const [matchedApp, gcsBase] = match;
  // Use API's next_app when available — it's the source of truth for which app
  // the member should use. Fall back to the URL-matched key otherwise.
  const resolveApp = nextApp && (nextApp in GCS_APP_DESTINATIONS) ? nextApp : matchedApp;
  const resolvedBase = await resolveDestination(resolveApp);

  // Strip a leading slash from subPath — bases without a trailing slash
  // (e.g. `/auditing/task-management`, `/web/admin`) would otherwise produce
  // `base//subPath` when joined.
  const subPath = pathOnly.slice(gcsBase.length).replace(/^\//, '');

  // Standard GCS rewrite — resolvedBase is the GCS relative path for resolveApp
  if (resolvedBase === GCS_APP_DESTINATIONS[resolveApp]) {
    const targetBase = GCS_APP_DESTINATIONS[resolveApp];
    const target = subPath ? targetBase.replace(/\/$/, '') + '/' + subPath : targetBase;
    return target + querySuffix;
  }

  // Generic rewrite — preserve the sub-path against whichever base resolveDestination
  // returned. Covers the legacy "flag OFF → absolute webserver URL" case and the
  // onboarding-RN override case (relative /onboarding/ base on the same GCS domain).
  const target = subPath ? resolvedBase.replace(/\/$/, '') + '/' + subPath : resolvedBase;
  return target + querySuffix;
}
